import React, { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  HandThumbUpIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import {
  EvidenceSignal,
  FunnelStage,
  ModelPerformanceData,
  OutcomeSlice,
  PerformanceRow,
  VisualToolRow,
  VisualToolSummary,
  useGetModelPerformanceFiltersQuery,
  useGetModelPerformanceQuery,
  useUpdateModelPricingMutation,
} from '../store/api/modelPerformanceApi'
import AgentLineupsTab from '../components/AgentLineupsTab'
import BusinessKnowledgeEventsTab from '../components/BusinessKnowledgeEventsTab'

// ─── Formatting ──────────────────────────────────────────────────────────────
//
// Every formatter renders null as "—". A rate with no denominator is unknown,
// and showing it as 0% would make an unmeasured model look like a failing one.

const TODAY = new Date().toISOString().slice(0, 10)

function pct(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

function usd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  if (value === 0) return '$0'
  if (value < 0.01) return `$${value.toFixed(5)}`
  if (value < 1) return `$${value.toFixed(4)}`
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function num(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toFixed(digits)
}

function count(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toLocaleString()
}

function duration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—'
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${s}s`
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  color,
  emphasis = false,
}: {
  label: string
  value: string
  sub?: string
  icon: React.ElementType
  color: string
  emphasis?: boolean
}) {
  return (
    <div
      className={`bg-white border rounded-xl shadow-sm p-5 flex items-start gap-4 ${
        emphasis ? 'border-indigo-300 ring-1 ring-indigo-100' : 'border-gray-200'
      }`}
    >
      <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
    </div>
  )
}

function Section({
  title,
  subtitle,
  children,
  right,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
  right?: React.ReactNode
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5 max-w-3xl">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  )
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-gray-500 font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 min-w-[10rem]"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-lg ${className}`} />
}

/**
 * The success funnel: implemented → technically verified → accepted → stable.
 *
 * The bar width is the rate over JUDGED runs, not over all runs. That choice is
 * the whole point: durability can only be judged a week after the fact, so
 * drawing it against all runs would show a collapse at the last rung that is
 * really just work still inside its window. The judged count is printed next to
 * every rung so a rate resting on nine runs cannot be mistaken for a trend.
 */
function FunnelView({ stages }: { stages: FunnelStage[] }) {
  const palette = ['bg-slate-400', 'bg-sky-500', 'bg-emerald-500', 'bg-indigo-500']

  return (
    <div className="space-y-4">
      {stages.map((stage, i) => (
        <div key={stage.key}>
          <div className="flex items-baseline justify-between gap-4 mb-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-900">{stage.label}</span>
              <span className="text-xs text-gray-400 truncate">{stage.note}</span>
            </div>
            <div className="flex items-baseline gap-2 flex-shrink-0">
              <span className="text-sm font-semibold text-gray-900">{pct(stage.rateOfJudged, 1)}</span>
              <span className="text-xs text-gray-500">
                {count(stage.reached)} of {count(stage.judged)} judged
              </span>
            </div>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${palette[i % palette.length]} rounded-full transition-all`}
              style={{ width: `${Math.min(100, (stage.rateOfJudged ?? 0) * 100)}%` }}
            />
          </div>
          {stage.judged === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              Nothing judged at this layer yet, so this rung is unknown rather than zero.
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

/** One layer's full distribution, so the funnel's single number has context. */
function OutcomeBreakdown({
  title,
  caption,
  slices,
}: {
  title: string
  caption: string
  slices: OutcomeSlice[]
}) {
  // Colouring by meaning rather than by position, so "failed" is never green
  // just because it happens to be the largest slice.
  const toneFor = (value: string): string => {
    if (/fully_verified|^accepted$|^stable$/.test(value)) return 'bg-emerald-500'
    if (/partially_verified|accepted_with_refinement/.test(value)) return 'bg-teal-400'
    if (/static_only|blocked_on_questions/.test(value)) return 'bg-sky-400'
    if (/failed|rejected|reverted|^error$/.test(value)) return 'bg-rose-500'
    if (/corrected|reopened/.test(value)) return 'bg-amber-500'
    return 'bg-gray-300'
  }

  return (
    <div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500 mb-3">{caption}</p>
      {slices.length ? (
        <div className="space-y-2">
          {slices.map((slice) => (
            <div key={slice.value} className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${toneFor(slice.value)}`} />
              <span className="text-xs text-gray-600 flex-1 truncate">
                {slice.value.replace(/_/g, ' ')}
              </span>
              <span className="text-xs text-gray-500 flex-shrink-0">{count(slice.runs)}</span>
              <span className="text-xs font-medium text-gray-900 w-10 text-right flex-shrink-0">
                {pct(slice.share)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No data.</p>
      )}
    </div>
  )
}

/**
 * Specialist evidence, grouped by agent.
 *
 * Deliberately renders whatever keys came back rather than a known list, since
 * the signals an agent reports are its own business and change as agents change.
 * Booleans show a rate, numbers show an average, and anything else shows only
 * how often it was reported — because averaging a label would be meaningless.
 */
/**
 * Specialized tools, shown apart from the reasoning-model tables above.
 *
 * The columns share nothing with those tables on purpose. An image generator has
 * no first-pass rate and no escalation history, because it never decided
 * anything — the UX agent did, and it is the agent's row above that should carry
 * the credit or the blame. What can fairly be asked of the tool is whether
 * reaching for it paid: how often the first render was the keeper, how often the
 * picture survived into the design and reached the Builder, and what a design a
 * human actually accepted cost to illustrate.
 */
function VisualToolsTable({
  rows,
  summary,
}: {
  rows: VisualToolRow[]
  summary: VisualToolSummary
}) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Generations', value: count(summary.calls), sub: `${count(summary.imagesGenerated)} images` },
          { label: 'Total spend', value: usd(summary.totalUsdSpend), sub: `${count(summary.tasksWithVisuals)} tasks used visuals` },
          { label: 'First render kept', value: pct(summary.firstSelectionRate), sub: `avg ${num(summary.avgIterations, 1)} iterations` },
          { label: 'Design accepted', value: pct(summary.designAcceptanceRate), sub: `${pct(summary.finalUsedRate)} reached final design` },
        ].map((kpi) => (
          <div key={kpi.label} className="border border-violet-200 bg-violet-50/40 rounded-lg p-3">
            <p className="text-xs text-violet-700 font-medium uppercase tracking-wide">{kpi.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5 leading-none">{kpi.value}</p>
            <p className="text-xs text-gray-500 mt-1">{kpi.sub}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
              <th className="px-2 py-2 font-medium">Tool</th>
              <th className="px-2 py-2 font-medium">Invoked by</th>
              <th className="px-2 py-2 font-medium text-right">Calls</th>
              <th className="px-2 py-2 font-medium text-right">Images</th>
              <th className="px-2 py-2 font-medium text-right" title="Generations that edited an existing screenshot rather than starting from a blank page">
                From ref
              </th>
              <th className="px-2 py-2 font-medium text-right">Avg iters</th>
              <th className="px-2 py-2 font-medium text-right" title="Of the selections made, how often iteration 1 was the one kept">
                1st kept
              </th>
              <th className="px-2 py-2 font-medium text-right" title="Share of judged generations that made it into the final design">
                Final used
              </th>
              <th className="px-2 py-2 font-medium text-right" title="Of the visuals in a final design, how many reached the Builder">
                To builder
              </th>
              <th className="px-2 py-2 font-medium text-right">Accepted</th>
              <th className="px-2 py-2 font-medium text-right">$ / gen</th>
              <th className="px-2 py-2 font-medium text-right" title="Rendering spend per design a human accepted">
                $ / accepted
              </th>
              <th className="px-2 py-2 font-medium text-right">Spend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={`${r.modelKey}-${r.agentKey}`} className="hover:bg-gray-50">
                <td className="px-2 py-2 font-medium text-gray-900 whitespace-nowrap">
                  {r.label || r.modelKey}
                  <span className="text-xs text-violet-600 ml-1.5">{r.provider ?? ''}</span>
                </td>
                <td className="px-2 py-2 text-gray-600 whitespace-nowrap">{r.agentKey}</td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {count(r.calls)}
                  {r.failedCalls > 0 && (
                    <span className="text-xs text-rose-600 ml-1">({count(r.failedCalls)} failed)</span>
                  )}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{count(r.imagesGenerated)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-gray-600">
                  {pct(r.calls ? r.imageToImageCalls / r.calls : null)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{num(r.avgIterations, 1)}</td>
                <td className="px-2 py-2 text-right tabular-nums font-medium text-gray-900">
                  {pct(r.firstSelectionRate)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{pct(r.finalUsedRate)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{pct(r.builderUsedRate)}</td>
                <td className="px-2 py-2 text-right tabular-nums font-medium text-emerald-700">
                  {pct(r.designAcceptanceRate)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{usd(r.avgUsdPerCall)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{usd(r.usdPerAcceptedDesign)}</td>
                <td className="px-2 py-2 text-right tabular-nums font-medium">{usd(r.usdCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EvidenceTable({ signals }: { signals: EvidenceSignal[] }) {
  const byAgent = useMemo(() => {
    const groups = new Map<string, EvidenceSignal[]>()
    for (const signal of signals) {
      const list = groups.get(signal.agentKey) ?? []
      list.push(signal)
      groups.set(signal.agentKey, list)
    }
    return [...groups.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [signals])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {byAgent.map(([agentKey, rows]) => (
        <div key={agentKey} className="border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">{agentKey}</p>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.signal} className="flex items-baseline gap-2">
                <span className="text-xs text-gray-600 flex-1 truncate" title={row.signal}>
                  {row.signal.replace(/_/g, ' ')}
                </span>
                <span className="text-xs font-medium text-gray-900 flex-shrink-0">
                  {row.kind === 'boolean'
                    ? pct(row.trueRate)
                    : row.kind === 'number'
                      ? num(row.avgValue, 1)
                      : '—'}
                </span>
                <span className="text-xs text-gray-400 w-12 text-right flex-shrink-0">
                  {count(row.runs)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * The three main tables (by model, by specialist, by task type) share this
 * component so the same metric always means the same thing wherever it appears.
 *
 * Sorted by cost per success by default — the number that answers "which model
 * gives the cheapest reliable result", which is the actual question. Rows with
 * no successes yet sort last rather than appearing free.
 */
function PerformanceTable({
  rows,
  keyHeader,
  loading,
}: {
  rows: PerformanceRow[]
  keyHeader: string
  loading: boolean
}) {
  const [sortKey, setSortKey] = useState<keyof PerformanceRow>('usdPerSuccess')
  const [ascending, setAscending] = useState(true)

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      // Nulls always sort last, in both directions: "not measured" is never the
      // best result.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'string' || typeof bv === 'string') {
        return ascending
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av))
      }
      return ascending ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return copy
  }, [rows, sortKey, ascending])

  function sortBy(key: keyof PerformanceRow, defaultAscending: boolean) {
    if (sortKey === key) {
      setAscending((prev) => !prev)
    } else {
      setSortKey(key)
      setAscending(defaultAscending)
    }
  }

  const columns: Array<{
    key: keyof PerformanceRow
    header: string
    title: string
    render: (row: PerformanceRow) => string
    ascendingIsBetter: boolean
  }> = [
    {
      key: 'runs',
      header: 'Runs',
      title: 'Units of agent work started on this model.',
      render: (r) => count(r.runs),
      ascendingIsBetter: false,
    },
    {
      key: 'resolvedRuns',
      header: 'Resolved',
      title: 'Runs with a known outcome. All rates below are computed over these only.',
      render: (r) => count(r.resolvedRuns),
      ascendingIsBetter: false,
    },
    {
      key: 'firstPassRate',
      header: 'First-pass',
      title: 'Succeeded with no escalation to another model. The cleanest quality signal.',
      render: (r) => pct(r.firstPassRate),
      ascendingIsBetter: false,
    },
    {
      key: 'finalSuccessRate',
      header: 'Final success',
      title: 'Succeeded eventually, including after being rescued by another model.',
      render: (r) => pct(r.finalSuccessRate),
      ascendingIsBetter: false,
    },
    {
      key: 'avgAttempts',
      header: 'Attempts',
      title: 'Average models tried per run (1 = never escalated).',
      render: (r) => num(r.avgAttempts),
      ascendingIsBetter: true,
    },
    {
      key: 'escalationRate',
      header: 'Escalation',
      title: 'How often this model had to hand off to another one.',
      render: (r) => pct(r.escalationRate),
      ascendingIsBetter: true,
    },
    {
      key: 'deliveryRate',
      header: 'Delivered',
      title: 'How often this model finished the work it started.',
      render: (r) => pct(r.deliveryRate),
      ascendingIsBetter: false,
    },
    {
      key: 'avgUsdPerRun',
      header: 'Cost/run',
      title: 'Includes the cost of any rescue this model triggered.',
      render: (r) => usd(r.avgUsdPerRun),
      ascendingIsBetter: true,
    },
    {
      key: 'usdPerSuccess',
      header: 'Cost/success',
      title: 'Total spend divided by successes — rescue cost included. The number to route on.',
      render: (r) => usd(r.usdPerSuccess),
      ascendingIsBetter: true,
    },
    {
      key: 'stableRate',
      header: 'Stable',
      title: 'Of runs judged retrospectively, how many survived without correction.',
      render: (r) => pct(r.stableRate),
      ascendingIsBetter: false,
    },
    {
      key: 'technicalVerifiedRate',
      header: 'Verified',
      title:
        'Of runs where verification ran at all, how many fully passed. Agents that produce documents rather than code are not judged here.',
      render: (r) => pct(r.technicalVerifiedRate),
      ascendingIsBetter: false,
    },
    {
      key: 'avgQualityScore',
      header: 'Score',
      title:
        'Blended 0–1 quality across the layers that resolved for each run. Layers nobody measured are excluded, never counted as zero.',
      render: (r) => num(r.avgQualityScore, 2),
      ascendingIsBetter: false,
    },
    {
      key: 'avgDurationMs',
      header: 'Avg time',
      title: 'Wall-clock duration of the run.',
      render: (r) => duration(r.avgDurationMs),
      ascendingIsBetter: true,
    },
    {
      key: 'usdCost',
      header: 'Spend',
      title: 'Total USD attributed to this row.',
      render: (r) => usd(r.usdCost),
      ascendingIsBetter: false,
    },
  ]

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    )
  }

  if (!rows.length) {
    return (
      <p className="text-sm text-gray-400 text-center py-8">
        No runs in this range yet. Telemetry only covers work done after the feature was deployed.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto -mx-2">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
            <th className="px-2 py-2 font-medium sticky left-0 bg-white">{keyHeader}</th>
            {columns.map((c) => (
              <th key={String(c.key)} className="px-2 py-2 font-medium text-right whitespace-nowrap">
                <button
                  type="button"
                  title={c.title}
                  onClick={() => sortBy(c.key, c.ascendingIsBetter)}
                  className={`hover:text-gray-800 ${sortKey === c.key ? 'text-indigo-600' : ''}`}
                >
                  {c.header}
                  {sortKey === c.key ? (ascending ? ' ↑' : ' ↓') : ''}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sorted.map((row) => (
            <tr key={row.key} className="hover:bg-gray-50">
              <td className="px-2 py-2 font-medium text-gray-900 sticky left-0 bg-white whitespace-nowrap">
                {row.label}
              </td>
              {columns.map((c) => (
                <td key={String(c.key)} className="px-2 py-2 text-right text-gray-700 whitespace-nowrap tabular-nums">
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2">
      <p className="font-medium text-gray-800 mb-1 text-sm">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? p.fill }} className="text-xs">
          {p.name}:{' '}
          <strong>
            {p.dataKey === 'usdCost' ? usd(p.value) : Number(p.value).toLocaleString()}
          </strong>
        </p>
      ))}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

const ALL = 'all'

type PageTab = 'performance' | 'lineups' | 'business_knowledge'

const ModelPerformance: React.FC = () => {
  // The two halves of one job: the numbers, and the decision the numbers exist
  // to inform. Kept on one page so nobody reorders a lineup without having just
  // looked at what it costs.
  const [activeTab, setActiveTab] = useState<PageTab>('performance')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState(TODAY)
  const [projectId, setProjectId] = useState(ALL)
  const [agentKey, setAgentKey] = useState(ALL)
  const [taskType, setTaskType] = useState(ALL)
  const [modelKey, setModelKey] = useState(ALL)
  const [complexity, setComplexity] = useState(ALL)
  const [performanceAxis, setPerformanceAxis] = useState<'primary' | 'research'>('primary')

  const [applied, setApplied] = useState<{ dateFrom: string; dateTo: string }>({
    dateFrom: '',
    dateTo: '',
  })
  const [hasSeededDates, setHasSeededDates] = useState(false)

  const { data: filterResp } = useGetModelPerformanceFiltersQuery()
  const options = filterResp?.data

  // Seed the date inputs from the real data range so the page does not open on
  // an empty window when telemetry has only just started collecting.
  useEffect(() => {
    if (!hasSeededDates && options?.dataRange?.firstDate) {
      setDateFrom(options.dataRange.firstDate)
      setDateTo(options.dataRange.lastDate ?? TODAY)
      setHasSeededDates(true)
    }
  }, [options, hasSeededDates])

  const queryParams = useMemo(
    () => ({
      dateFrom: applied.dateFrom || undefined,
      dateTo: applied.dateTo || undefined,
      projectId: projectId === ALL ? undefined : projectId,
      agentKey: agentKey === ALL ? undefined : agentKey,
      taskType: taskType === ALL ? undefined : taskType,
      modelKey: modelKey === ALL ? undefined : modelKey,
      complexity: complexity === ALL ? undefined : complexity,
      axis: performanceAxis,
    }),
    [applied, projectId, agentKey, taskType, modelKey, complexity, performanceAxis]
  )

  const { data: resp, isLoading, isFetching, isError, refetch } = useGetModelPerformanceQuery(queryParams)
  const d: ModelPerformanceData | undefined = resp?.data
  const loading = isLoading || isFetching

  const [updatePricing, { isLoading: savingPrice }] = useUpdateModelPricingMutation()
  const [pricingDraft, setPricingDraft] = useState<Record<string, { in: string; out: string }>>({})

  // The registry's price editor is per-token, which is meaningless for a tool
  // billed per image, so the two kinds get their own listings.
  const reasoningCatalog = useMemo(
    () => (d?.catalog ?? []).filter((m) => (m.capability ?? 'reasoning') === 'reasoning'),
    [d?.catalog],
  )
  const specializedCatalog = useMemo(
    () => (d?.catalog ?? []).filter((m) => (m.capability ?? 'reasoning') !== 'reasoning'),
    [d?.catalog],
  )

  async function savePricing(key: string, currentIn: number | null, currentOut: number | null) {
    const draft = pricingDraft[key]
    const parse = (raw: string | undefined, fallback: number | null): number | null => {
      if (raw === undefined || raw.trim() === '') return fallback
      const n = Number(raw)
      return Number.isFinite(n) && n >= 0 ? n : fallback
    }
    try {
      await updatePricing({
        modelKey: key,
        usdInPerMtok: parse(draft?.in, currentIn),
        usdOutPerMtok: parse(draft?.out, currentOut),
      }).unwrap()
      toast.success(`Saved pricing for ${key}. Applies to calls recorded from now on.`)
    } catch (err) {
      toast.error(`Could not save pricing for ${key}.`)
    }
  }

  const projectOptions = useMemo(
    () => [
      { value: ALL, label: 'All projects' },
      ...(options?.projects ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    ],
    [options]
  )

  const trendData = useMemo(() => d?.trend ?? [], [d])

  return (
    <div className="w-full max-w-[1700px] mx-auto space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Model Performance</h1>
          <p className="text-sm text-gray-500 mt-0.5 max-w-3xl">
            Which model gives Motionode the cheapest reliable result, per agent and per kind of work.
            Cost is charged to the model a run <em>started</em> on, so a cheap model that keeps needing
            rescue shows its true price.
          </p>
        </div>
        {activeTab === 'performance' && (
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3.5 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className="border-b border-gray-200 flex gap-6">
        {([
          { id: 'performance' as PageTab, label: 'Performance' },
          { id: 'lineups' as PageTab, label: 'Agent lineups' },
          { id: 'business_knowledge' as PageTab, label: 'Business Knowledge' },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'performance' && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">View</span>
          {([
            { id: 'primary' as const, label: 'Primary' },
            { id: 'research' as const, label: 'Research' },
          ]).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setPerformanceAxis(opt.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                performanceAxis === opt.id
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-800'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {performanceAxis === 'research' ? (
            <span className="text-xs text-gray-500 ml-2">
              Research contribution metrics (parent judgments). Shadow only — does not control routing.
            </span>
          ) : null}
        </div>
      )}

      {activeTab === 'lineups' && <AgentLineupsTab />}
      {activeTab === 'business_knowledge' && <BusinessKnowledgeEventsTab />}

      {activeTab === 'performance' && (
      <>
      {/* ── Filters ── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex items-end gap-3 flex-wrap">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
            <CalendarIcon className="w-3.5 h-3.5" /> From
          </span>
          <input
            type="date"
            value={dateFrom}
            max={TODAY}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500 font-medium">To</span>
          <input
            type="date"
            value={dateTo}
            max={TODAY}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </label>

        <Select label="Project" value={projectId} onChange={setProjectId} options={projectOptions} />
        <Select
          label="Specialist"
          value={agentKey}
          onChange={setAgentKey}
          options={[
            { value: ALL, label: 'All specialists' },
            ...(options?.agents ?? []).map((a) => ({ value: a, label: a })),
          ]}
        />
        <Select
          label="Task type"
          value={taskType}
          onChange={setTaskType}
          options={[
            { value: ALL, label: 'All task types' },
            ...(options?.taskTypes ?? []).map((t) => ({ value: t, label: t })),
          ]}
        />
        <Select
          label="Model"
          value={modelKey}
          onChange={setModelKey}
          options={[
            { value: ALL, label: 'All models' },
            ...(options?.models ?? []).map((m) => ({ value: m, label: m })),
          ]}
        />
        <Select
          label="Complexity"
          value={complexity}
          onChange={setComplexity}
          options={[
            { value: ALL, label: 'Any complexity' },
            ...(options?.complexities ?? []).map((c) => ({ value: c, label: c })),
          ]}
        />

        <button
          onClick={() => setApplied({ dateFrom, dateTo })}
          className="px-3.5 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Apply dates
        </button>
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          Could not load model performance. Check the admin API logs — a query may be failing (this is usually not a missing migration).
        </div>
      )}

      {/* ── Caveats ── */}
      {!!d?.caveats?.length && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900">How to read these numbers</p>
              <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                {d.caveats.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ── Summary cards ── */}
      {loading && !d ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        d && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard
              label="AI runs"
              value={count(d.summary.totalRuns)}
              sub={`${count(d.summary.resolvedRuns)} with a known outcome`}
              icon={ChartBarIcon}
              color="bg-indigo-500"
            />
            <KpiCard
              label="Successful"
              value={count(d.summary.successfulRuns)}
              sub={`${count(d.summary.unresolvedRuns)} still unresolved`}
              icon={CheckCircleIcon}
              color="bg-emerald-500"
            />
            <KpiCard
              label="First-pass success"
              value={pct(d.summary.firstPassRate, 1)}
              sub="No escalation needed"
              icon={SparklesIcon}
              color="bg-teal-500"
            />
            <KpiCard
              label="Final success"
              value={pct(d.summary.finalSuccessRate, 1)}
              sub="Including rescues"
              icon={ArrowTrendingUpIcon}
              color="bg-sky-500"
            />
            <KpiCard
              label="Cost / operational success"
              value={usd(d.summary.usdPerSuccess)}
              sub="Start-model view; rescue cost included"
              icon={CurrencyDollarIcon}
              color="bg-violet-600"
              emphasis
            />
            <KpiCard
              label="Avg cost / run"
              value={usd(d.summary.avgUsdPerRun)}
              sub="Cost of choosing starter (incl. rescue)"
              icon={BanknotesIcon}
              color="bg-fuchsia-500"
            />
            <KpiCard
              label="Avg attempts"
              value={num(d.summary.avgAttempts)}
              sub="Models tried per run"
              icon={ArrowPathIcon}
              color="bg-amber-500"
            />
            <KpiCard
              label="Escalation rate"
              value={pct(d.summary.escalationRate, 1)}
              sub="Runs needing a rescue"
              icon={BoltIcon}
              color="bg-orange-500"
            />
            <KpiCard
              label="Avg run time"
              value={duration(d.summary.avgDurationMs)}
              icon={ClockIcon}
              color="bg-slate-500"
            />
            <KpiCard
              label="Total AI spend"
              value={usd(d.summary.totalUsdSpend)}
              sub={`${count(d.summary.totalCredits)} credits billed`}
              icon={CpuChipIcon}
              color="bg-gray-700"
            />
          </div>
        )
      )}

      {/* ── Evidence strength ── */}
      {d && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex items-center gap-3">
          <HandThumbUpIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <p className="text-sm text-gray-600">
            <strong className="text-gray-900">{pct(d.summary.humanRatedRate, 1)}</strong> of resolved runs
            carry an explicit thumbs rating. The rest are inferred from what people did next — follow-up
            corrections, step returns, and sending work onward without changing it. Inferred signals are
            weighted lower than a real click.
          </p>
        </div>
      )}

      {/* ── Success funnel ── */}
      <Section
        title="Success funnel"
        subtitle="Success is not one boolean. Each rung is measured only over the runs where that layer has an answer, because the layers resolve on different schedules — the build result is known at once, acceptance on the next interaction, durability a week later."
      >
        {d?.funnel?.length ? (
          <FunnelView stages={d.funnel} />
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">No runs in this range yet.</p>
        )}
      </Section>

      {/* ── Outcome distributions ── */}
      <Section
        title="Where runs actually land"
        subtitle="The full distribution behind the funnel. Reading these together is what separates “the code ran” from “the request was satisfied” from “it lasted”."
      >
        {d ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <OutcomeBreakdown
              title="Technical"
              caption="Did it build, lint and test?"
              slices={d.outcomes.technical}
            />
            <OutcomeBreakdown
              title="Task"
              caption="Did it satisfy what was asked?"
              slices={d.outcomes.task}
            />
            <OutcomeBreakdown
              title="Durability"
              caption="Did it survive afterwards?"
              slices={d.outcomes.stability}
            />
          </div>
        ) : (
          <Skeleton className="h-40 w-full" />
        )}
      </Section>

      {/* ── Trend ── */}
      <Section
        title="Volume and spend over time"
        subtitle="From the daily rollups, so this reaches further back than the raw call history. Runs are counted on the model a run started on."
      >
        {trendData.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="runs" name="Runs" stroke="#6366f1" strokeWidth={2} dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="calls" name="LLM calls" stroke="#14b8a6" strokeWidth={2} dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="usdCost" name="USD spend" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            No rollups for this range yet. They are computed nightly.
          </p>
        )}
      </Section>

      {/* ── Model comparison ── */}
      {performanceAxis === 'research' ? (
        <Section
          title="Research delegate comparison"
          subtitle="Grouped by parent agent + delivering research model. Used rate is parent contribution judgment — not verified correctness. No branch text is exposed."
        >
          {(d as any)?.exploration?.byAgent?.length ? (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-1">Controlled exploration (filter window)</h3>
              <p className="text-xs text-gray-500 mb-3">
                Share = exploratory first attempts / research first attempts (failover excluded).
                Challenger delivered = exploratory start that finished on the challenger (not failover).
                Low Security share is expected — not an automatic warning. Distinct from ranking&apos;s explorationNeeded diagnostic.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                      <th className="py-2 pr-3">Agent</th>
                      <th className="py-2 pr-3">Share</th>
                      <th className="py-2 pr-3">Starts</th>
                      <th className="py-2 pr-3">Challenger delivered</th>
                      <th className="py-2 pr-3">Judged</th>
                      <th className="py-2 pr-3">Judged yield</th>
                      <th className="py-2 pr-3">Challenger fail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((d as any).exploration.byAgent as any[]).map((r) => (
                      <tr key={r.agentKey} className="border-b border-gray-100">
                        <td className="py-2 pr-3 font-medium text-gray-800">{r.agentKey}</td>
                        <td className="py-2 pr-3 tabular-nums">{r.explorationSharePct == null ? '—' : `${r.explorationSharePct}%`}</td>
                        <td className="py-2 pr-3 tabular-nums">{count(r.explorationStarts)}</td>
                        <td className="py-2 pr-3 tabular-nums">{count(r.challengerDelivered)}</td>
                        <td className="py-2 pr-3 tabular-nums">{count(r.exploratoryJudged)}</td>
                        <td className="py-2 pr-3 tabular-nums">{r.judgedYieldPct == null ? '—' : `${r.judgedYieldPct}%`}</td>
                        <td className="py-2 pr-3 tabular-nums">{r.challengerFailureRate == null ? '—' : pct(r.challengerFailureRate, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          {(d as any)?.byModel?.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                    <th className="py-2 pr-3">Agent</th>
                    <th className="py-2 pr-3">Model</th>
                    <th className="py-2 pr-3">Used</th>
                    <th className="py-2 pr-3">Judged</th>
                    <th className="py-2 pr-3">Contradicted</th>
                    <th className="py-2 pr-3">USD / used</th>
                    <th className="py-2 pr-3">Speed</th>
                    <th className="py-2 pr-3">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {((d as any).byModel as any[]).map((r) => (
                    <tr key={r.key || `${r.agentKey}-${r.modelKey}`} className="border-b border-gray-100">
                      <td className="py-2 pr-3 font-medium text-gray-800">{r.agentKey}</td>
                      <td className="py-2 pr-3 text-gray-800">{r.label || r.modelKey}</td>
                      <td className="py-2 pr-3 tabular-nums">{r.usedRate == null ? '—' : `${r.usedRate}%`}</td>
                      <td className="py-2 pr-3 tabular-nums">{count(r.evaluated)}</td>
                      <td className="py-2 pr-3 tabular-nums">{r.contradictedRate == null ? '—' : `${r.contradictedRate}%`}</td>
                      <td className="py-2 pr-3 tabular-nums">{usd(r.usdPerUsed)}</td>
                      <td className="py-2 pr-3 tabular-nums">{duration(r.avgLatencyMs)}</td>
                      <td className="py-2 pr-3 tabular-nums">{r.evaluationCoverage == null ? '—' : pct(r.evaluationCoverage, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(d as any)?.summary?.note ? (
                <p className="text-xs text-gray-500 mt-3">{(d as any).summary.note}</p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">
              {(d as any)?.summary?.note || 'No research rollups for this range yet.'}
            </p>
          )}
        </Section>
      ) : (
      <>
      <Section
        title="Delivery Performance"
        subtitle="Grouped by final_model_key — the model that delivered. Split by lineup tier (primary can own a task; delegate takes sub-work). Within each tier, ordered by performance (quality per dollar, then accepted rate) — same direction as adaptive ranking. Cost is MAIN+RESCUE only."
      >
        {(d as any)?.byDeliveryModel?.length ? (
          <div className="space-y-6">
            {(
              [
                {
                  tier: 'primary' as const,
                  title: 'Primary',
                  note: 'Models that may own a task in at least one specialist lineup.',
                },
                {
                  tier: 'delegate' as const,
                  title: 'Delegate',
                  note: 'Models used for sub-tasks handed off by a primary — not task owners.',
                },
                {
                  tier: 'unlisted' as const,
                  title: 'Not in lineups',
                  note: 'Delivered runs on models that are not in the current primary/delegate lineups.',
                },
              ] as const
            ).map(({ tier, title, note }) => {
              const rows = ((d as any).byDeliveryModel as any[]).filter(
                (r) => (r.tier || 'unlisted') === tier,
              )
              if (!rows.length) return null
              return (
                <div key={tier}>
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-500">{note}</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                          <th className="py-2 pr-3">Model</th>
                          <th className="py-2 pr-3">Delivered</th>
                          <th className="py-2 pr-3">Judged</th>
                          <th className="py-2 pr-3">Accepted</th>
                          <th className="py-2 pr-3">Accepted rate</th>
                          <th className="py-2 pr-3">Quality</th>
                          <th className="py-2 pr-3">USD / accepted</th>
                          <th className="py-2 pr-3">Tech success</th>
                          <th className="py-2 pr-3">Latency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.key} className="border-b border-gray-100">
                            <td className="py-2 pr-3 font-medium text-gray-800">{r.label || r.key}</td>
                            <td className="py-2 pr-3 tabular-nums">{count(r.deliveredRuns)}</td>
                            <td className="py-2 pr-3 tabular-nums">{count(r.taskJudgedRuns)}</td>
                            <td className="py-2 pr-3 tabular-nums">{count(r.acceptedRuns)}</td>
                            <td className="py-2 pr-3 tabular-nums">{pct(r.acceptedRate, 0)}</td>
                            <td className="py-2 pr-3 tabular-nums">{num(r.avgQualityScore, 3)}</td>
                            <td className="py-2 pr-3 tabular-nums">{usd(r.usdPerAccepted)}</td>
                            <td className="py-2 pr-3 tabular-nums">{pct(r.technicalSuccessRate, 0)}</td>
                            <td className="py-2 pr-3 tabular-nums">{duration(r.avgDurationMs)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
            <p className="text-xs text-gray-500">
              Tech success is technical verification only — not the same as router acceptedRate.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">No delivery rows for this range.</p>
        )}
      </Section>
      <Section
        title="Start-model / routing-decision analytics"
        subtitle="Grouped by the model each run started on — the cost of choosing that starter. A run's whole cost, including any rescue it triggered, is charged here. Not the same as Delivery Performance above."
      >
        <PerformanceTable rows={d?.byModel ?? []} keyHeader="Model" loading={loading && !d} />
      </Section>

      {/* ── By specialist ── */}
      <Section
        title="Performance by specialist"
        subtitle="Mo, AI Builder, Detective, Designer and the rest measure different things, so compare models within a specialist rather than across them."
      >
        <PerformanceTable rows={d?.bySpecialist ?? []} keyHeader="Specialist" loading={loading && !d} />
      </Section>

      {/* ── By task type ── */}
      <Section
        title="Performance by task type"
        subtitle="Labels are assigned by a cheap classifier and are free-form on purpose — Motionode hosts unrelated projects, so no fixed task list would fit for long. Filter to one specialist to see where a model actually wins."
      >
        <PerformanceTable rows={d?.byTaskType ?? []} keyHeader="Task type" loading={loading && !d} />
      </Section>
      </>
      )}

      {/* ── Escalation matrix ── */}
      <Section
        title="Routing and escalations"
        subtitle="After a model fails, which one actually rescues it. Rescue rate is measured over runs with a known outcome."
      >
        {d?.escalationMatrix?.length ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                    <th className="px-2 py-2 font-medium">Failed model → rescue model</th>
                    <th className="px-2 py-2 font-medium text-right">Escalations</th>
                    <th className="px-2 py-2 font-medium text-right">Rescued</th>
                    <th className="px-2 py-2 font-medium text-right">Rescue rate</th>
                    <th className="px-2 py-2 font-medium text-right">Rescue spend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {d.escalationMatrix.map((e) => (
                    <tr key={`${e.fromModel}->${e.toModel}`} className="hover:bg-gray-50">
                      <td className="px-2 py-2 text-gray-900 whitespace-nowrap">
                        <span className="text-gray-500">{e.fromModel}</span>
                        <span className="mx-1.5 text-gray-300">→</span>
                        <span className="font-medium">{e.toModel}</span>
                      </td>
                      <td className="px-2 py-2 text-right text-gray-700 tabular-nums">{count(e.escalations)}</td>
                      <td className="px-2 py-2 text-right text-gray-700 tabular-nums">{count(e.rescued)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        <span
                          className={
                            e.rescueRate == null
                              ? 'text-gray-400'
                              : e.rescueRate >= 0.8
                                ? 'text-emerald-600 font-medium'
                                : e.rescueRate >= 0.5
                                  ? 'text-amber-600'
                                  : 'text-red-600'
                          }
                        >
                          {pct(e.rescueRate)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right text-gray-700 tabular-nums">{usd(e.usdCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <ResponsiveContainer width="100%" height={Math.max(220, d.escalationMatrix.length * 34)}>
                <BarChart
                  data={d.escalationMatrix.slice(0, 12).map((e) => ({
                    name: `${e.fromModel} → ${e.toModel}`,
                    escalations: e.escalations,
                    rescued: e.rescued,
                  }))}
                  layout="vertical"
                  margin={{ left: 8, right: 16 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="name" width={190} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="escalations" name="Escalations" fill="#cbd5e1" />
                  <Bar dataKey="rescued" name="Rescued" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            No model escalations recorded in this range. Either routing is holding, or nothing needed a rescue.
          </p>
        )}
      </Section>

      {/* ── Recommendations ── */}
      <Section
        title="Model recommendations"
        subtitle="Informational only — nothing routes on this yet. Shown only for combinations with at least 8 resolved runs, because advice drawn from three runs is noise."
      >
        {d?.recommendations?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {d.recommendations.map((r) => (
              <div
                key={`${r.agentKey}-${r.taskType ?? 'any'}`}
                className="border border-gray-200 rounded-lg p-4 hover:border-indigo-200 transition-colors"
              >
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {r.agentKey}
                  {r.taskType ? ` · ${r.taskType}` : ''}
                </p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{r.recommendedModel}</p>
                <div className="flex items-center gap-3 mt-1 text-sm">
                  <span className="text-emerald-600 font-medium">{pct(r.firstPassRate)} first-pass</span>
                  <span className="text-gray-500">{usd(r.usdPerSuccess)} / success</span>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{r.rationale}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            Not enough resolved runs yet to recommend anything. Recommendations appear once a
            specialist and task type have 8 or more runs with a known outcome on the same model.
          </p>
        )}
      </Section>

      {/* ── Specialist evidence ── */}
      <Section
        title="Specialist evidence"
        subtitle="What each agent proved about its own output. These signals are not declared anywhere — agents report whatever objective facts they have, and this table is built from whatever turned up, so a new agent appears here on its first run."
      >
        {d?.evidence?.length ? (
          <EvidenceTable signals={d.evidence} />
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            No evidence reported in this range yet.
          </p>
        )}
      </Section>

      {/* ── Specialized tools ── */}
      <Section
        title="Specialized tools"
        subtitle="Tools an agent invokes to produce something, kept out of the model comparisons above and out of routing. An image generator draws what the UX agent decided; it did not decide anything, so ranking it against Sonnet or Grok would be comparing a printer to an author. These numbers answer a narrower question: when an agent reached for the tool, did it pay off?"
        right={
          <span className="text-xs px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1.5">
            <PhotoIcon className="w-3.5 h-3.5" />
            Not ranked against reasoning models
          </span>
        }
      >
        {d?.visualTools?.length ? (
          <VisualToolsTable rows={d.visualTools} summary={d.visualSummary} />
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            No specialized tool calls in this range. Visual generation is invoked by the UX agent only
            when a picture would help, so quiet periods here are expected rather than a fault.
          </p>
        )}
      </Section>

      {/* ── Model registry ── */}
      <Section
        title="Model registry"
        subtitle="Reasoning models, registered automatically the first time they are used, so this list grows on its own. A model with no USD price contributes $0 to cost and will win every comparison unfairly — set its price here. Edits apply to calls recorded from then on; historical costs are left as recorded. Specialized tools are priced per image rather than per token and are listed separately below."
      >
        {reasoningCatalog.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                  <th className="px-2 py-2 font-medium">Model</th>
                  <th className="px-2 py-2 font-medium">Provider</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Price source</th>
                  <th className="px-2 py-2 font-medium text-right">$ / M in</th>
                  <th className="px-2 py-2 font-medium text-right">$ / M out</th>
                  <th className="px-2 py-2 font-medium">First seen</th>
                  <th className="px-2 py-2 font-medium">Last seen</th>
                  <th className="px-2 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reasoningCatalog.map((m) => {
                  const unpriced = m.usdInPerMtok == null && m.usdOutPerMtok == null
                  const draft = pricingDraft[m.modelKey]
                  return (
                    <tr key={m.modelKey} className={unpriced ? 'bg-amber-50/50' : 'hover:bg-gray-50'}>
                      <td className="px-2 py-2 font-medium text-gray-900 whitespace-nowrap">
                        {m.label || m.modelKey}
                        {m.label && <span className="text-xs text-gray-400 ml-1.5">{m.modelKey}</span>}
                      </td>
                      <td className="px-2 py-2 text-gray-600">{m.provider ?? '—'}</td>
                      <td className="px-2 py-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            m.status === 'active'
                              ? 'bg-emerald-100 text-emerald-700'
                              : m.status === 'deprecated'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {m.status}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500">
                        {m.pricingSource === 'seed_estimate' ? 'estimate' : m.pricingSource}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          defaultValue={m.usdInPerMtok ?? ''}
                          placeholder="unpriced"
                          onChange={(e) =>
                            setPricingDraft((prev) => ({
                              ...prev,
                              [m.modelKey]: { in: e.target.value, out: draft?.out ?? '' },
                            }))
                          }
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          defaultValue={m.usdOutPerMtok ?? ''}
                          placeholder="unpriced"
                          onChange={(e) =>
                            setPricingDraft((prev) => ({
                              ...prev,
                              [m.modelKey]: { in: draft?.in ?? '', out: e.target.value },
                            }))
                          }
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-500">{m.firstSeenAt ?? '—'}</td>
                      <td className="px-2 py-2 text-xs text-gray-500">{m.lastSeenAt ?? '—'}</td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          disabled={savingPrice || !draft}
                          onClick={() => savePricing(m.modelKey, m.usdInPerMtok, m.usdOutPerMtok)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">
            The registry is empty. It fills in on the first catalog sync after boot.
          </p>
        )}

        {specializedCatalog.length > 0 && (
          <div className="mt-6 pt-5 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-900">Specialized tools</p>
            <p className="text-xs text-gray-500 mb-3">
              Billed per output rather than per token, and never offered to an agent as a reasoning
              model. The rate shown is nominal — the exact price of a generation depends on the
              quality and resolution the agent asked for, and is recorded per call.
            </p>
            <div className="flex flex-wrap gap-2">
              {specializedCatalog.map((m) => (
                <span
                  key={m.modelKey}
                  className="text-xs px-3 py-1.5 rounded-lg bg-violet-50 border border-violet-200 text-violet-900"
                >
                  <span className="font-medium">{m.label || m.modelKey}</span>
                  <span className="text-violet-500 ml-1.5">{m.provider ?? ''}</span>
                  <span className="text-gray-500 ml-2">
                    {m.usdPerUnit == null ? 'unpriced' : `${usd(m.usdPerUnit)} / ${m.pricingUnit}`}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </Section>
      </>
      )}
    </div>
  )
}

export default ModelPerformance
